'use client';

import { useState, useCallback } from 'react';
import type { ChatSummary } from '../types';

interface UseChatListOptions {
  apiBaseUrl?: string;
}

export function useChatList(options: UseChatListOptions = {}) {
  const { apiBaseUrl = '/api/meridian' } = options;
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();

  const loadChats = useCallback(
    async (status: 'active' | 'archived' | 'all' = 'active') => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          status,
          limit: '20',
        });
        const response = await fetch(`${apiBaseUrl}/chats?${params}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success) {
          setChats(data.data.chats);
          setCursor(data.data.cursor);
          setHasMore(data.data.hasMore);
        } else {
          setError(data.error);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load chats');
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl]
  );

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    try {
      const params = new URLSearchParams({
        limit: '20',
        cursor,
      });
      const response = await fetch(`${apiBaseUrl}/chats?${params}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setChats((prev) => [...prev, ...data.data.chats]);
        setCursor(data.data.cursor);
        setHasMore(data.data.hasMore);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more chats');
    }
  }, [cursor, apiBaseUrl]);

  return { chats, isLoading, error, hasMore, loadChats, loadMore };
}
